import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { EnvelopeIcon, PhoneIcon, MapPinIcon } from '@heroicons/react/24/outline';

const contactDetails = [
  {
    icon: <MapPinIcon className="h-6 w-6 text-accent" />,
    label: 'Our Location',
    value: '115–117 William Angliss Drive, Laverton North, Victoria 3026',
    href: null,
  },
  {
    icon: <EnvelopeIcon className="h-6 w-6 text-accent" />,
    label: 'Email Us',
    value: 'bookingsupport@clutchit.com.au',
    href: 'mailto:bookingsupport@clutchit.com.au',
  },
  {
    icon: <PhoneIcon className="h-6 w-6 text-accent" />,
    label: 'Call Us',
    value: '+61 438 925 410',
    href: 'tel:+61438925410',
  },
];

const inputClass =
  'w-full rounded-xl border border-border-medium bg-surface-1 text-primary placeholder:text-secondary px-4 py-2.5 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent';

const labelClass = 'block text-sm font-medium text-primary mb-1.5';

const Contact = () => {
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);

    try {
      const response = await fetch('https://formspree.io/f/xldevoov', {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
      });

      if (response.ok) {
        setStatus('success');
        form.reset();
      } else {
        const json = await response.json();
        setStatus(json.errors ? json.errors.map((err) => err.message).join(', ') : 'error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="section-padding bg-white relative overflow-hidden">
      <div className="absolute inset-0 bg-mesh-grid pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-subtle to-transparent" />

      <div className="section-container relative">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="section-label">
            <EnvelopeIcon className="w-4 h-4" />
            Contact
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            Get in<br />
            <span className="text-gradient-blue">Touch With Us</span>
          </h1>
          <p className="text-secondary text-lg max-w-2xl mx-auto">
            Ready to streamline your logistics operations? Reach out for a free consultation and our team will get back to you promptly.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">

          {/* Left — contact details */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 flex flex-col gap-4"
          >
            {contactDetails.map(({ icon, label, value, href }) => (
              <div
                key={label}
                className="rounded-2xl border border-border-subtle bg-white p-5 shadow-card flex items-start gap-4 group hover:border-accent hover:shadow-glow-sm transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/20 transition-colors duration-300">
                  {icon}
                </div>
                <div>
                  <p className="text-primary font-semibold text-sm mb-0.5">{label}</p>
                  {href ? (
                    <a
                      href={href}
                      className="text-secondary text-sm hover:text-accent transition-colors duration-200"
                    >
                      {value}
                    </a>
                  ) : (
                    <p className="text-secondary text-sm leading-relaxed">{value}</p>
                  )}
                </div>
              </div>
            ))}

            {/* Response time note */}
            <div className="rounded-2xl border border-border-subtle bg-white p-5 shadow-card flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-primary font-semibold text-sm mb-0.5">Response Time</p>
                <p className="text-secondary text-sm leading-relaxed">We aim to respond to all enquiries within one business day.</p>
              </div>
            </div>
          </motion.div>

          {/* Right — form */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-3 rounded-2xl border border-border-subtle bg-white p-8 shadow-card"
          >
            <h2 className="text-primary font-bold text-xl mb-6">Send us a message</h2>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="firstName" className={labelClass}>First name <span className="text-accent">*</span></label>
                  <input type="text" id="firstName" name="firstName" className={inputClass} required />
                </div>
                <div>
                  <label htmlFor="lastName" className={labelClass}>Last name <span className="text-accent">*</span></label>
                  <input type="text" id="lastName" name="lastName" className={inputClass} required />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="email" className={labelClass}>Email address <span className="text-accent">*</span></label>
                  <input type="email" id="email" name="email" className={inputClass} required />
                </div>
                <div>
                  <label htmlFor="mobile" className={labelClass}>Mobile</label>
                  <input type="tel" id="mobile" name="mobile" className={inputClass} />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className={labelClass}>Subject</label>
                <input type="text" id="subject" name="subject" className={inputClass} />
              </div>

              <div>
                <label htmlFor="message" className={labelClass}>Message <span className="text-accent">*</span></label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  className={`${inputClass} resize-none`}
                  required
                />
              </div>

              <button type="submit" className="btn-primary justify-center">
                Send Message
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                </svg>
              </button>

              {status === 'success' && (
                <motion.p
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-center text-green-600 dark:text-green-400 font-medium"
                >
                  Thanks for your message — we'll be in touch shortly!
                </motion.p>
              )}
              {status === 'error' && (
                <motion.p
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-center text-red-500 font-medium"
                >
                  Oops! There was a problem submitting your form. Please try again.
                </motion.p>
              )}
            </form>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Contact;
